import { useEffect } from 'react'
import { SignIn, SignUp, useAuth, useUser } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAppStore } from '@/store/app'
import Layout from '@/components/Layout'
import OnboardingWizard from '@/pages/OnboardingWizard'
import Dashboard from '@/pages/Dashboard'
import MealBuilder from '@/pages/MealBuilder'
import ShoppingListPage from '@/pages/ShoppingListPage'
import SettingsPage from '@/pages/SettingsPage'
import LoadingScreen from '@/components/LoadingScreen'

export default function App() {
  const { isSignedIn, isLoaded, getToken } = useAuth()
  const { user } = useUser()
  const { view, setView, hasCompletedOnboarding, setHasCompletedOnboarding, page, setPage, activePlanId, setActivePlanId } = useAppStore()

  useEffect(() => {
    if (!isSignedIn || !user) return
    getToken().then((token) => {
      if (!token) return
      api.post('/users/sync', {
        email: user.primaryEmailAddress?.emailAddress ?? '',
        display_name: user.fullName,
      }, token).catch(console.error)
    })
  }, [isSignedIn, user, getToken])

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const token = await getToken()
      return api.get('/users/me', token!)
    },
    enabled: !!isSignedIn,
  })

  useEffect(() => {
    if (!profileData) return
    const profile = (profileData as { data: { profile: { diet_types: string[]; calorie_target: number | null; goal_mode: string } | null } }).data?.profile
    // A profile is considered onboarded if any preference was explicitly saved.
    // We check calorie_target, diet_types, OR goal_mode being 'macros' (which
    // is only set during onboarding). Only promote to true — never demote back
    // to false so that a background refetch can't kick the user out mid-session.
    const onboarded = profile && (
      profile.diet_types?.length > 0 ||
      profile.calorie_target != null ||
      profile.goal_mode === 'macros'
    )
    if (onboarded) setHasCompletedOnboarding(true)
  }, [profileData, setHasCompletedOnboarding])

  if (!isLoaded || (isSignedIn && profileLoading)) return <LoadingScreen />

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">MealMap</h1>
            <p className="text-brand-100">Plan meals. Shop smarter.</p>
          </div>
          {view === 'sign-up' ? (
            <>
              <SignUp routing="hash" />
              <p className="text-center text-sm text-brand-100 mt-4">
                Already have an account?{' '}
                <button onClick={() => setView('sign-in')} className="text-white underline">Sign in</button>
              </p>
            </>
          ) : (
            <>
              <SignIn routing="hash" />
              <p className="text-center text-sm text-brand-100 mt-4">
                Don't have an account?{' '}
                <button onClick={() => setView('sign-up')} className="text-white underline">Sign up</button>
              </p>
            </>
          )}
        </div>
      </div>
    )
  }

  if (!hasCompletedOnboarding) {
    return <OnboardingWizard onComplete={() => setHasCompletedOnboarding(true)} />
  }

  return (
    <Layout>
      {page === 'meal-builder' && activePlanId ? (
        <MealBuilder
          planId={activePlanId}
          onBack={() => { setPage('dashboard'); setActivePlanId(null) }}
          onProceedToShopping={(id) => { setActivePlanId(id); setPage('shopping-list') }}
        />
      ) : page === 'shopping-list' && activePlanId ? (
        <ShoppingListPage
          planId={activePlanId}
          onBack={() => setPage('meal-builder')}
        />
      ) : page === 'settings' ? (
        <SettingsPage onBack={() => setPage('dashboard')} />
      ) : (
        <Dashboard
          onOpenPlan={(id) => { setActivePlanId(id); setPage('meal-builder') }}
        />
      )}
    </Layout>
  )
}
